import type { ReactNode } from 'react'
import { toast } from 'sonner'
import type { EventPrivacy } from '~/features/events/lib/event-privacy'
import { formIssues } from '~/features/events/lib/form-issues'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import type { OccurrenceForm } from '~/shared/types/event'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { EventPreview } from './event-preview'

interface EventFormAction {
  label: string
  busyLabel: string
  busy: boolean
  disabled: boolean
  onClick: () => void
  blockedHint?: string
}

interface EventFormSubmitState {
  canSubmit: boolean
  isSubmitting: boolean
  errors: unknown[]
  fieldMeta: Record<string, { errors: unknown[] } | undefined>
  privacy: EventPrivacy
}

interface EventFormActions {
  primary: EventFormAction
  secondary?: EventFormAction
}

interface EventFormLayoutProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
  form: any
  coverImageUrl?: string
  actions: (state: EventFormSubmitState) => EventFormActions
  onCancel: () => void
  children: ReactNode
}

const MAX_LISTED_ISSUES = 5

/** Create/edit event page body: the sections in one column, preview and actions beside them from xl, a sticky action bar below. */
export function EventFormLayout({
  form,
  coverImageUrl,
  actions,
  onCancel,
  children,
}: EventFormLayoutProps) {
  const withActions = (render: (resolved: EventFormActions, issues: FormIssues) => ReactNode) => (
    <form.Subscribe
      selector={(
        state: Omit<EventFormSubmitState, 'privacy'> & { values: { privacy: EventPrivacy } },
      ) => ({
        canSubmit: state.canSubmit,
        isSubmitting: state.isSubmitting,
        errors: state.errors,
        fieldMeta: state.fieldMeta,
        privacy: state.values.privacy,
      })}
    >
      {(state: EventFormSubmitState) => {
        const resolved = actions(state)
        return render(resolved, issuesOf(state, resolved.primary.blockedHint))
      }}
    </form.Subscribe>
  )

  return (
    <div className="mx-auto w-full max-w-7xl">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)] xl:items-start">
        <div className="min-w-0 space-y-6">{children}</div>
        <aside className="hidden space-y-4 xl:sticky xl:top-6 xl:block">
          <LivePreview form={form} coverImageUrl={coverImageUrl} />
          <Card>
            <CardContent className="space-y-4 p-4">
              <VisibilitySummary form={form} />
              {withActions(({ primary, secondary }, issues) => (
                <>
                  <IssueList issues={issues} />
                  <div className="flex flex-col gap-2">
                    <ActionButton action={primary} variant="default" className="h-10 w-full" />
                    <div className="flex gap-2">
                      {secondary && (
                        <ActionButton action={secondary} variant="secondary" className="flex-1" />
                      )}
                      <Button type="button" variant="ghost" className="flex-1" onClick={onCancel}>
                        {m.cancel()}
                      </Button>
                    </div>
                  </div>
                </>
              ))}
            </CardContent>
          </Card>
        </aside>
      </div>
      <div className="sticky bottom-0 z-20 -mx-4 mt-6 border-t bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 xl:hidden">
        {withActions(({ primary, secondary }, issues) => (
          <>
            {issues.items.length > 0 && (
              <p
                className={cn(
                  'mb-2 truncate text-sm',
                  issues.invalid ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {issues.items.length > 1
                  ? `${issues.items[0]} · ${m.andNMore({ count: issues.items.length - 1 })}`
                  : issues.items[0]}
              </p>
            )}
            <div className="flex items-center gap-2 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                className="hidden sm:mr-auto sm:inline-flex"
                onClick={onCancel}
              >
                {m.cancel()}
              </Button>
              {secondary && (
                <ActionButton
                  action={secondary}
                  variant="outline"
                  className="flex-1 sm:flex-none"
                />
              )}
              <ActionButton action={primary} variant="default" className="flex-1 sm:flex-none" />
            </div>
          </>
        ))}
      </div>
    </div>
  )
}

const FIELD_LABELS: Record<string, () => string> = {
  title: m.eventTitle,
  description: m.description,
  location: m.location,
  'location.street': m.streetAddress,
  'location.city': m.city,
  'location.country': m.country,
  startTime: m.eventFieldDates,
  endTime: m.eventFieldDates,
  occurrences: m.eventFieldDates,
  tags: m.eventFieldCategory,
  price: m.price,
  ticketUrl: m.eventFieldTicketLink,
  ticketTypes: m.ticketsTitle,
  capacity: m.eventFieldCapacity,
  targetAgeMin: m.eventFieldAge,
  targetAgeMax: m.eventFieldAge,
}

function fieldLabel(field: string): string | undefined {
  return FIELD_LABELS[field]?.()
}

interface FormIssues {
  items: string[]
  /** A field holds a wrong value; otherwise the items only say what is still missing. */
  invalid: boolean
}

function issuesOf(state: EventFormSubmitState, blockedHint?: string): FormIssues {
  const issues = formIssues(state, fieldLabel)
  return {
    items: blockedHint ? [...issues, blockedHint] : issues,
    invalid: issues.length > 0,
  }
}

function showBlockedReason(hint: string) {
  document
    .querySelector('[data-field="images"]')
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  toast.info(hint)
}

function ActionButton({
  action,
  variant,
  className,
}: {
  action: EventFormAction
  variant: 'default' | 'secondary' | 'outline'
  className: string
}) {
  const { blockedHint } = action
  return (
    <Button
      type="button"
      variant={variant}
      className={className}
      disabled={action.disabled}
      aria-disabled={blockedHint ? true : undefined}
      onClick={blockedHint ? () => showBlockedReason(blockedHint) : action.onClick}
    >
      {action.busy ? action.busyLabel : action.label}
    </Button>
  )
}

function IssueList({ issues: { items, invalid } }: { issues: FormIssues }) {
  if (items.length === 0) return null
  return (
    <div
      role={invalid ? 'alert' : 'status'}
      data-form-issues
      className={cn(
        'squircle rounded-lg p-3',
        invalid ? 'border border-destructive/50 bg-destructive/10' : 'bg-muted',
      )}
    >
      <p className={cn('font-medium text-sm', invalid ? 'text-destructive' : 'text-foreground')}>
        {invalid ? m.pleaseCompleteTheseRequiredFields() : m.eventFormStillOpen()}
      </p>
      <ul
        className={cn(
          'mt-1 list-inside list-disc text-xs',
          invalid ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {items.slice(0, MAX_LISTED_ISSUES).map((issue) => (
          <li key={issue}>{issue}</li>
        ))}
        {items.length > MAX_LISTED_ISSUES && (
          <li>{m.andNMore({ count: items.length - MAX_LISTED_ISSUES })}</li>
        )}
      </ul>
    </div>
  )
}

function VisibilitySummary({
  form,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
  form: any
}) {
  return (
    <form.Subscribe
      selector={(state: { values: { privacy: EventPrivacy } }) => state.values.privacy}
    >
      {(privacy: EventPrivacy) => (
        <div className="flex items-center justify-between gap-4 text-sm">
          <span className="text-muted-foreground">{m.eventVisibilityLabel()}</span>
          <span className="font-medium">
            {privacy === 'private'
              ? m.eventVisibilityPrivate()
              : privacy === 'members'
                ? m.eventVisibilityMembers()
                : privacy === 'request'
                  ? m.eventVisibilityRequest()
                  : m.eventVisibilityPublic()}
          </span>
        </div>
      )}
    </form.Subscribe>
  )
}

interface PreviewValues {
  title: string
  description: string
  startTime: string
  occurrences: OccurrenceForm[]
  location: { name?: string; street: string; city: string; country: string }
  price: {
    kind: 'unknown' | 'free' | 'fixed' | 'range'
    priceFixed?: number
    priceRangeMin?: number
    priceRangeMax?: number
  }
}

function LivePreview({
  form,
  coverImageUrl,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
  form: any
  coverImageUrl?: string
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {m.preview()}
      </p>
      <form.Subscribe
        selector={(state: { values: PreviewValues }) => ({
          title: state.values.title,
          description: state.values.description,
          startTime: state.values.occurrences?.[0]?.date || state.values.startTime,
          location: state.values.location,
          price: state.values.price,
        })}
      >
        {(values: Omit<PreviewValues, 'occurrences'>) => (
          <EventPreview {...values} coverImageUrl={coverImageUrl} />
        )}
      </form.Subscribe>
    </div>
  )
}
